import { useEffect, useState, type JSX } from 'react';
import type { SnapshotRow } from '@cortex/styling-pattern-watch-core';
import { fetchSnapshots } from '../api.js';
import type { DashboardPayload } from '../dashboard-types.js';
import { SvgLineChart, type ChartSeries } from './SvgLineChart.js';
import { buildHistoryChartData, rangeFromIso, type HistoryRange } from './history.js';

interface Props {
  data: DashboardPayload;
  baseUrl: string;
  fetchImpl?: typeof fetch;
  /** テスト用: now を固定 */
  now?: Date;
}

/** @graph-connects none */
const RANGE_OPTIONS: { k: HistoryRange; label: string }[] = [
  { k: 'today', label: '今日（3時間ごと）' },
  { k: '7d', label: '過去7日（日次）' },
  { k: '14d', label: '過去14日（日次）' },
  { k: '30d', label: '過去30日（日次）' },
];

/** @graph-connects none */
const CATEGORY_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
] as const;

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 時系列履歴ビュー。/v1/snapshots level=category を呼び、選択 range のバケットに整形して SVG ライングラフでカテゴリ別に描画する
 * @graph-connects fetchSnapshots [calls] スナップショット履歴取得 API
 * @graph-connects buildHistoryChartData [calls] バケット整形ユーティリティ
 * @graph-connects ChartCardBody [calls] チャート描画
 */
export function HistoryView(props: Props): JSX.Element {
  const [range, setRange] = useState<HistoryRange>('today');
  const [rows, setRows] = useState<SnapshotRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const now = props.now ?? new Date();
    setLoading(true);
    setError(null);
    fetchSnapshots(
      {
        level: 'category',
        fromIso: rangeFromIso(range, now),
        limit: 2000,
      },
      { baseUrl: props.baseUrl, fetchImpl: props.fetchImpl },
    )
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, props.baseUrl, props.fetchImpl, props.now]);

  const now = props.now ?? new Date();
  const chart =
    rows === null
      ? null
      : buildHistoryChartData({
          rows,
          range,
          categories: props.data.categories,
          now,
        });

  return (
    <div className="history-section">
      <div className="section-head">
        <div>
          <h2 className="section-title">
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path d="M2 12l3-4 3 2 5-6" />
              <path d="M2 14h12" />
            </svg>
            選定可能型数の時系列履歴
          </h2>
          <p className="section-desc">カテゴリ別のスナップショット履歴を時系列で表示します。</p>
        </div>
      </div>

      <div className="history-controls">
        <div className="filter-group">
          <span className="filter-label">期間:</span>
          {RANGE_OPTIONS.map((option) => {
            const handleClick = (): void => {
              setRange(option.k);
            };
            return (
              <button
                key={option.k}
                type="button"
                className={`chip-btn ${range === option.k ? 'active' : ''}`}
                onClick={handleClick}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card">
          <ChartCardBody
            loading={loading}
            error={error}
            chart={chart}
            categories={props.data.categories}
          />
        </div>
      </div>
    </div>
  );
}

interface ChartCardBodyProps {
  loading: boolean;
  error: string | null;
  chart: ReturnType<typeof buildHistoryChartData> | null;
  categories: DashboardPayload['categories'];
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business HistoryView のチャートカード描画を担当する内部コンポーネント。ロード中・エラー・空・正常の4状態を切り替え、正常時はカテゴリ別 SvgLineChart を描画する
 * @graph-connects SvgLineChart [calls] チャート描画
 */
function ChartCardBody(props: ChartCardBodyProps): JSX.Element {
  if (props.error !== null) {
    return (
      <>
        <div className="chart-card-head">
          <span className="chart-card-title">時系列データの取得に失敗しました</span>
        </div>
        <p className="section-desc">{props.error}</p>
      </>
    );
  }
  if (props.loading || props.chart === null) {
    return (
      <div className="chart-card-head">
        <span className="chart-card-title">読み込み中…</span>
      </div>
    );
  }
  if (props.chart.isEmpty) {
    return (
      <>
        <div className="chart-card-head">
          <span className="chart-card-title">この期間のスナップショットはありません</span>
        </div>
        <p className="section-desc">
          範囲を広げるか、styling-pattern-watch transformer の実行履歴を確認してください。
        </p>
      </>
    );
  }
  const series: ChartSeries[] = props.chart.series.map((s, i) => {
    return {
      name: props.categories[i].name,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
      values: s.values,
    };
  });
  return (
    <>
      <div className="chart-card-head">
        <span className="chart-card-title">選定可能型数（カテゴリ別）</span>
      </div>
      <SvgLineChart labels={props.chart.buckets.map((b) => b.label)} series={series} />
    </>
  );
}
